import { Col, Container, Row, Badge, Spinner, Card, CardBody, Button, Image } from 'react-bootstrap';
import Link from 'next/link';
import { useEastCretePage, getSectionByKey } from '@/hooks/useEastCretePage';
import { FaHotel, FaRoute } from 'react-icons/fa';
import { getMediaUrl } from '@/utils/imageUrl';

const HotelsExcursionsPromo = ({ sectionData: propSectionData }) => {
  const { sections, loading } = useEastCretePage();
  const section = propSectionData || getSectionByKey(sections, 'hotels_excursions_promo');

  const bgColor = section?.background_color || 'light';
  const bgEffect = section?.background_effect || null;
  let bgClass = bgColor === 'primary' ? 'section-bg-primary' : bgColor === 'white' ? 'bg-white' : 'bg-light';
  // Apply gradient effect if selected
  if (bgColor === 'primary' && bgEffect === 'gradient') {
    bgClass = 'section-bg-primary-gradient';
  }

  if (loading && !propSectionData) {
    return (
      <section className={`py-4 py-lg-5 ${bgClass} hotels-excursions-promo-section`}>
        <Container>
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        </Container>
      </section>
    );
  }

  if (!section || !section.is_active) {
    return null;
  }

  const items = section.items || [];
  const hotelsItem = items.find(item => item.item_type === 'card' && (item.title?.toLowerCase().includes('hotel') || item.content?.title?.toLowerCase().includes('hotel'))) || items[0];
  const excursionsItem = items.find(item => item.item_type === 'card' && (item.title?.toLowerCase().includes('excursion') || item.content?.title?.toLowerCase().includes('excursion'))) || items[1];

  const hasDotsEffect = bgColor === 'primary' && bgEffect === 'dots';

  // Parse item content
  const parseItemContent = (item) => {
    if (!item?.content) return {};
    if (typeof item.content === 'string') {
      try {
        return JSON.parse(item.content);
      } catch {
        return {};
      }
    }
    return item.content || {};
  };

  // Get hotels card data
  const hotelsContent = parseItemContent(hotelsItem);
  const hotelsTitle = hotelsItem?.title || hotelsContent?.title || 'Discover Our Hotels';
  const hotelsDescription = hotelsItem?.description || hotelsContent?.description || 'Explore our carefully selected accommodation options across East Crete, from luxury resorts to charming boutique hotels.';
  const hotelsImage = hotelsItem?.image || hotelsContent?.image || null;
  const hotelsImageSrc = hotelsImage ? getMediaUrl(hotelsImage) : null;
  const hotelsImageStyle = hotelsContent?.image_style || (hotelsImage ? 'background' : 'header');
  const hotelsLink = hotelsItem?.link || hotelsContent?.link || '/hotels';
  const hotelsLinkText = hotelsItem?.link_text || hotelsContent?.link_text || 'Browse Hotels';

  // Get excursions card data
  const excursionsContent = parseItemContent(excursionsItem);
  const excursionsTitle = excursionsItem?.title || excursionsContent?.title || 'Explore Our Excursions';
  const excursionsDescription = excursionsItem?.description || excursionsContent?.description || 'Discover the beauty of Crete with our curated selection of guided tours and experiences throughout the region.';
  const excursionsImage = excursionsItem?.image || excursionsContent?.image || null;
  const excursionsImageSrc = excursionsImage ? getMediaUrl(excursionsImage) : null;
  const excursionsImageStyle = excursionsContent?.image_style || (excursionsImage ? 'background' : 'header');
  const excursionsLink = excursionsItem?.link || excursionsContent?.link || '/excursions';
  const excursionsLinkText = excursionsItem?.link_text || excursionsContent?.link_text || 'View Excursions';

  return (
    <section className={`py-4 py-lg-5 ${bgClass} hotels-excursions-promo-section ${hasDotsEffect ? 'position-relative overflow-hidden' : ''}`}>
      {hasDotsEffect && (
        <>
          <span className="section-bg-dots section-bg-dots--left d-none d-xl-block" />
          <span className="section-bg-dots section-bg-dots--right d-none d-lg-block" />
        </>
      )}
      <Container className={hasDotsEffect ? 'position-relative' : ''}>
        {/* Header Section */}
        <Row className="mb-4">
          <Col>
            {section.subtitle && (
              <Badge bg="primary" className="bg-opacity-10 text-primary mb-3 px-3 py-2">
                {section.subtitle}
              </Badge>
            )}
            {section.title && (
              <h2 className="sec-title mb-3">
                {section.title}
              </h2>
            )}
            {section.intro_text && (
              <p className="lead intro-text mb-0" dangerouslySetInnerHTML={{ __html: section.intro_text }} />
            )}
          </Col>
        </Row>

        {/* Hotels & Excursions Cards */}
        <Row className="g-4">
          {/* Hotels Card */}
          <Col lg={6}>
            <Card className={`border-0 shadow-sm h-100 promo-card ${hotelsImageSrc && hotelsImageStyle === 'header' ? 'promo-card--header-style' : ''}`}>
              {hotelsImageSrc && hotelsImageStyle === 'background' && (
                <>
                  <div className="promo-card-bg" style={{ backgroundImage: `url(${hotelsImageSrc})` }} />
                  <div className="promo-card-overlay" />
                </>
              )}
              {hotelsImageSrc && hotelsImageStyle === 'header' && (
                <div className="promo-card-header-image">
                  <Image src={hotelsImageSrc} alt={hotelsTitle} className="w-100" />
                </div>
              )}
              {!hotelsImageSrc && <div className="promo-card-overlay" />}
              <CardBody className={`promo-card-content position-relative ${hotelsImageSrc && hotelsImageStyle === 'background' ? 'text-white' : ''} ${hotelsImageSrc && hotelsImageStyle === 'header' ? 'promo-card-content--header' : ''}`}>
                {hotelsImageSrc && hotelsImageStyle === 'header' ? (
                  <div className="promo-card-header-row d-flex align-items-center gap-3 mb-3">
                    <div className="promo-card-icon promo-card-icon--no-bg flex-shrink-0">
                      <FaHotel size={32} />
                    </div>
                    <h4 className="fw-bold mb-0 flex-grow-1">
                      {hotelsTitle}
                    </h4>
                  </div>
                ) : (
                  <>
                    <div className={`promo-card-icon mb-3 ${hotelsImageSrc && hotelsImageStyle === 'background' ? '' : 'promo-card-icon--no-bg'}`}>
                      <FaHotel size={32} />
                    </div>
                    <h4 className={`fw-bold mb-3 ${hotelsImageSrc && hotelsImageStyle === 'background' ? 'text-white' : ''}`}>
                      {hotelsTitle}
                    </h4>
                  </>
                )}
                <p className={`mb-4 ${hotelsImageSrc && hotelsImageStyle === 'background' ? 'text-white-75' : ''}`} style={{ lineHeight: '1.6' }} dangerouslySetInnerHTML={{ __html: hotelsDescription }} />
                <Button 
                  as={Link} 
                  href={hotelsLink} 
                  variant={hotelsImageSrc && hotelsImageStyle === 'background' ? 'light' : 'primary'} 
                  size="lg"
                  className="promo-card-button"
                >
                  {hotelsLinkText}
                </Button>
              </CardBody>
            </Card>
          </Col>

          {/* Excursions Card */}
          <Col lg={6}>
            <Card className={`border-0 shadow-sm h-100 promo-card ${excursionsImageSrc && excursionsImageStyle === 'header' ? 'promo-card--header-style' : ''}`}>
              {excursionsImageSrc && excursionsImageStyle === 'background' && (
                <>
                  <div className="promo-card-bg" style={{ backgroundImage: `url(${excursionsImageSrc})` }} />
                  <div className="promo-card-overlay" />
                </>
              )}
              {excursionsImageSrc && excursionsImageStyle === 'header' && (
                <div className="promo-card-header-image">
                  <Image src={excursionsImageSrc} alt={excursionsTitle} className="w-100" />
                </div>
              )}
              {!excursionsImageSrc && <div className="promo-card-overlay" />}
              <CardBody className={`promo-card-content position-relative ${excursionsImageSrc && excursionsImageStyle === 'background' ? 'text-white' : ''} ${excursionsImageSrc && excursionsImageStyle === 'header' ? 'promo-card-content--header' : ''}`}>
                {excursionsImageSrc && excursionsImageStyle === 'header' ? (
                  <div className="promo-card-header-row d-flex align-items-center gap-3 mb-3">
                    <div className="promo-card-icon promo-card-icon--no-bg flex-shrink-0">
                      <FaRoute size={32} />
                    </div>
                    <h4 className="fw-bold mb-0 flex-grow-1">
                      {excursionsTitle}
                    </h4>
                  </div>
                ) : (
                  <>
                    <div className={`promo-card-icon mb-3 ${excursionsImageSrc && excursionsImageStyle === 'background' ? '' : 'promo-card-icon--no-bg'}`}>
                      <FaRoute size={32} />
                    </div>
                    <h4 className={`fw-bold mb-3 ${excursionsImageSrc && excursionsImageStyle === 'background' ? 'text-white' : ''}`}>
                      {excursionsTitle}
                    </h4>
                  </>
                )}
                <p className={`mb-4 ${excursionsImageSrc && excursionsImageStyle === 'background' ? 'text-white-75' : ''}`} style={{ lineHeight: '1.6' }} dangerouslySetInnerHTML={{ __html: excursionsDescription }} />
                <Button 
                  as={Link} 
                  href={excursionsLink} 
                  variant={excursionsImageSrc && excursionsImageStyle === 'background' ? 'light' : 'primary'} 
                  size="lg"
                  className="promo-card-button"
                >
                  {excursionsLinkText}
                </Button>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default HotelsExcursionsPromo;
